/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, throttle } from 'lodash-es';
import { Icon } from '@fuxi/eevee-icon';
import { Decimal } from 'decimal.js';
import toastify from '@/utils/toastify';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { genMinMaxSteps } from '@/components/stepper';
import { Toast } from '@/utils';
import { PosAdjust, ScrollView, SlideSelect, Stepper, When } from '@/components';
import { SocketClientType } from '@/service/socketio';
import { controItemExtranDom } from '../../massage-setting/utils';
import { MassageControlValueContext } from '../index';
import cx from './index.module.scss';

interface Props {
  socketInstance: React.MutableRefObject<SocketClientType>;
  className?: string;
  controlItems: GetMassageControlPannelResponse[0]['controlItemConfigs'];
  massageState: RobotState;
  massageTechValues: CommonConfigResponse['values'];
  noduleLength: number;
  currentIsHitLine: boolean;
  lineHits: number;
  intensifyTreatmentStatus: GetMachineStateResponse['intensify_treatment_status'];
  isSkipping: boolean;
  onStepperValueChange: VoidFunc;
}

const DEBOUNCE_TIME = 300;

export const formatCommonValues = (values: CommonConfigResponse['values']) => (value: string) => {
  return values?.find(item => +item.value === +value)?.label || value;
};

const ParamsAdjuster: React.FC<Props> = ({
  className,
  controlItems,
  socketInstance,
  massageState,
  massageTechValues,
  noduleLength,
  currentIsHitLine,
  intensifyTreatmentStatus,
  lineHits,
  isSkipping,
  onStepperValueChange,
}) => {
  const { allValues, setAllValues, getAllValues, isDisconnectMachineArm } = useContext(MassageControlValueContext);
  const isSendingNoduleHitsRequest = useRef(false);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [isTouchAddPainPoint, setIsTouchAddPainPoint] = useState(false);
  const [isExecutingIntensifyTreatment, setIsExecutingIntensifyTreatment] = useState(false);
  const [currentAdjustingItem, setCurrentAdjustingItem] = useState<Massage.ControlItemOptions>(null);

  // 调节手法、跳过、痛点加强时禁止调节参数
  const currentIsDisabledAdjust = useMemo(
    () =>
      (currentAdjustingItem && ['massage.tech', 'moxibustion.tech', 'hot_stone.mode'].includes(currentAdjustingItem)) ||
      isExecutingIntensifyTreatment ||
      isSkipping,
    [currentAdjustingItem, isExecutingIntensifyTreatment, isSkipping]
  );

  // 防抖，力度
  const debounceSetForce = useMemo(
    () =>
      debounce(async (value: number) => {
        await socketInstance.current.setForce({ force: value });
        setCurrentAdjustingItem(null);
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，冲击波强度
  const debounceSetShockPower = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.setShockwave({ power: value });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，筋膜宝电流强度
  const debounceSetHotStonePower = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.setHotstonePower({
            power: value ? new Decimal(value).div(100).toNumber() : value,
          });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，筋膜宝电温度
  const debounceSetHotStoneTemperature = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.setHotstoneTemperatureLevel({ temperature_level: value });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，冲击波频率
  const debounceSetShockFrequency = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.setShockwave({ frequency: value });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，射频能量
  const debounceSetRfPower = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.setRadioFrequency({ power: +new Decimal(value).div(100).toFixed(2) });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，高度
  const debounceSetHeight = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.setHeight({ height: value });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  // 防抖，速度
  const debounceSetVelocity = useMemo(
    () =>
      debounce(async (value: number) => {
        try {
          await socketInstance.current.switchVelocity({ velocity: value });
        } finally {
          setCurrentAdjustingItem(null);
        }
      }, DEBOUNCE_TIME),
    [socketInstance]
  );

  const handleOnChange = useCallback(
    async (activeConfigItem: Massage.ControlItemOptions, value: any) => {
      onStepperValueChange?.();
      setCurrentAdjustingItem(activeConfigItem);

      switch (activeConfigItem) {
        case 'massage.tech':
        case 'moxibustion.tech':
          try {
            await socketInstance.current.switchMassageTech(value);
            setAllValues(prev => ({ ...prev, [activeConfigItem]: value }));
          } catch (e) {
            console.error(e);
          } finally {
            setCurrentAdjustingItem(null);
          }
          return;
        case 'robot.position':
          try {
            await socketInstance.current.move({ direction: value });
          } catch (e) {
            console.error(e);
          } finally {
            setCurrentAdjustingItem(null);
          }
          return;
        // 设置结节
        case 'shockwave.nodule_hits':
          try {
            if (isSendingNoduleHitsRequest.current) {
              return;
            }
            // 服务中打击总次数 = 走线总次数 + 单个痛点打击次数 * 计算总痛点个数
            const noduleHitsTotalCount = lineHits + value * noduleLength;
            if (noduleHitsTotalCount >= 25000) {
              Toast.show('本次方案打击次数已达到25000次，建议调整打击次数，以免影响设备寿命');
            }

            isSendingNoduleHitsRequest.current = true;
            await socketInstance.current.setShockwaveNoduleHits({ value });

            setAllValues(prev => ({ ...prev, [activeConfigItem]: value }));

            toastify.showNoduleHitInfo(`痛点打击总次数：${value * noduleLength}次`);
          } catch (e) {
            console.error(e);
          } finally {
            setCurrentAdjustingItem(null);
            isSendingNoduleHitsRequest.current = false;
          }
          return;
        case 'hot_stone.mode':
          try {
            await socketInstance.current.switchHotstoneMode({ mode: value });
            setAllValues(prev => ({ ...prev, [activeConfigItem]: value }));
          } catch (e) {
            console.error(e);
          } finally {
            setCurrentAdjustingItem(null);
          }
          break;
        case 'hot_stone.power':
          debounceSetHotStonePower(value);
          break;
        case 'hot_stone.temperature_level':
          debounceSetHotStoneTemperature(value);
          break;
        case 'robot.force':
          debounceSetForce(value);
          break;
        case 'shockwave.power':
          debounceSetShockPower(value);
          break;
        case 'shockwave.frequency':
          debounceSetShockFrequency(value);
          break;
        case 'radio_frequency.power':
          debounceSetRfPower(value);
          break;
        case 'robot.height':
          debounceSetHeight(value);
          break;
        case 'robot.velocity':
          debounceSetVelocity(value);
          break;
      }

      if (getAllValues()[activeConfigItem] !== value) {
        setAllValues(prev => ({ ...prev, [activeConfigItem]: value }));
      }
    },
    [
      onStepperValueChange,
      getAllValues,
      debounceSetHotStonePower,
      debounceSetHotStoneTemperature,
      debounceSetForce,
      debounceSetShockPower,
      debounceSetShockFrequency,
      debounceSetRfPower,
      debounceSetHeight,
      debounceSetVelocity,
      socketInstance,
      setAllValues,
      lineHits,
      noduleLength,
    ]
  );

  const handleAddNodule = useCallback(() => {
    // 正在调节其它项的时候禁用痛点加强治疗
    if (currentIsDisabledAdjust || !!currentAdjustingItem) return;

    setIsExecutingIntensifyTreatment(true);
    socketInstance.current.executeIntensifyTreatment().finally(() => {
      setIsExecutingIntensifyTreatment(false);
    });
  }, [currentAdjustingItem, currentIsDisabledAdjust, socketInstance]);

  const filteredControlItems = useMemo(
    () =>
      controlItems
        ?.filter(item => !!controItemExtranDom[item.name]?.label)
        ?.filter(item =>
          item.name === 'shockwave.nodule_hits'
            ? allValues?.['shockwave.nodule_hits'] && currentIsHitLine
              ? item
              : false
            : !!item
        ),
    [controlItems, allValues, currentIsHitLine]
  );

  const throttleRefresh = useMemo(
    () =>
      throttle(() => {
        scrollViewRef.current?.handleRefresh?.();
      }, 3000),
    []
  );

  useEffect(() => {
    if (filteredControlItems?.length) {
      const timer = setTimeout(() => scrollViewRef.current?.handleRefresh?.(), 500);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [filteredControlItems?.length]);

  return (
    <ScrollView ref={scrollViewRef} className={cx('params-adjuster', className)}>
      <div onTouchStart={throttleRefresh}>
        {filteredControlItems?.map(item => (
          <div key={item.name} className={cx('setting-item', { 'position-item': item.name === 'robot.position' })}>
            <div className={cx('label', { disabled: isDisconnectMachineArm || massageState !== 'Running_Massaging' })}>
              {item.name === 'massage.intensify_treatment' && intensifyTreatmentStatus?.times ? (
                <div>
                  局部加强
                  <br />
                  治疗<span className={cx('color-blue')}>{intensifyTreatmentStatus.times}</span>次
                </div>
              ) : (
                controItemExtranDom[item.name]?.label
              )}
              <When condition={item.name === 'hot_stone.power' && (allValues['hot_stone.power'] as number) >= 60}>
                <Icon name="electric" className={cx('electric-icon')} />
              </When>
            </div>
            <div className={cx('setter')}>
              {['moxibustion.tech', 'massage.tech', 'hot_stone.mode'].includes(item.name) ? (
                <div className={cx('tech-wrapper')}>
                  <SlideSelect
                    activeColor="white"
                    key={item.name}
                    activeBold={true}
                    steps={item.name === 'hot_stone.mode' ? item.values : massageTechValues}
                    disabledForAdjust={currentIsDisabledAdjust || !!currentAdjustingItem}
                    disabled={isDisconnectMachineArm || massageState !== 'Running_Massaging'}
                    value={allValues[item.name]}
                    onChange={value => handleOnChange(item.name, value)}
                  />
                </div>
              ) : item.name === 'massage.intensify_treatment' ? (
                <div className={cx('intensify-treatment')}>
                  <div
                    onTouchStart={() => massageState === 'Running_Massaging' && setIsTouchAddPainPoint(true)}
                    onTouchEnd={() => massageState === 'Running_Massaging' && setIsTouchAddPainPoint(false)}
                    onTouchCancel={() => setIsTouchAddPainPoint(false)}
                    className={cx('btn', {
                      disabled:
                        isDisconnectMachineArm ||
                        isExecutingIntensifyTreatment ||
                        massageState !== 'Running_Massaging' ||
                        intensifyTreatmentStatus?.times === intensifyTreatmentStatus?.max_times,
                      touch: isTouchAddPainPoint,
                    })}
                    onClick={handleAddNodule}
                  >
                    <Icon name="wave" color="#2d6efa" className={cx('wave-icon')} />
                    <span>多按一会</span>
                  </div>
                  <div className={cx('info')}>
                    <Icon className={cx('info-icon')} name="info-circle" />
                    <span>最多可在理疗过程中加强{intensifyTreatmentStatus?.max_times}次</span>
                  </div>
                </div>
              ) : item.name === 'robot.position' ? (
                <PosAdjust
                  onChange={value => handleOnChange('robot.position', value)}
                  disabledForAdjust={currentIsDisabledAdjust}
                  disabled={isDisconnectMachineArm || massageState !== 'Running_Massaging'}
                />
              ) : (
                <Stepper
                  isShowHotStonePowerConfirm={true}
                  ignoreValueDisabled={[
                    'robot.velocity',
                    'robot.force',
                    'shockwave.frequency',
                    'shockwave.power',
                    'radio_frequency.power',
                    'robot.height',
                    'hot_stone.power',
                    'hot_stone.temperature_level',
                  ].includes(item.name)}
                  {...genMinMaxSteps(item.values)}
                  disabledForAdjust={currentIsDisabledAdjust}
                  disabled={
                    allValues[item.name] === undefined ||
                    allValues[item.name] === null ||
                    massageState !== 'Running_Massaging' ||
                    isDisconnectMachineArm
                  }
                  value={allValues[item.name] as number}
                  propertyName={item.name}
                  onChange={value => handleOnChange(item.name, value)}
                  suffix={controItemExtranDom[item.name]?.suffix}
                  formatText={controItemExtranDom[item.name]?.formatText || formatCommonValues(item.values)}
                />
              )}
              <When condition={item.name === 'hot_stone.power' && (allValues['hot_stone.power'] as number) >= 60}>
                <div className={cx('hot-stone-power-tips')}>
                  <Icon name="tips" className={cx('icon')} />
                  <span>强度60%及以上酥麻感较强，请沟通后使用</span>
                </div>
              </When>
            </div>
          </div>
        ))}
      </div>
    </ScrollView>
  );
};

export default ParamsAdjuster;
